import { useQuery } from '@tanstack/react-query';
import { PlusIcon, SquareCheckBigIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/shared/auth/store';
import { useSidePanel } from '@/shared/components/side-panel';
import { matchesQuery, normalizeQuery } from '@/shared/lib/strings';
import type { CommandItem } from '@/shared/registry/types';

import { tasksApi, useTaskCounters } from '../api';
import { useOpenNewTask } from '../hooks';

/** Sidebar badge: overdue tasks + tasks with unread comments (refreshed by realtime events). */
export function useTasksNavCounter(): number | undefined {
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  return useTaskCounters({ enabled: signedIn }).data?.total;
}

/** ⌘K: tasks found by title or number, and "New task" while the query is empty or names it. */
export function useTaskCommands(query: string): { items: CommandItem[]; isLoading?: boolean } {
  const { t } = useTranslation('tasks');
  const openNew = useOpenNewTask();
  const { open } = useSidePanel();
  const search = normalizeQuery(query) ?? '';
  const enabled = search.length >= 2;
  const found = useQuery({
    queryKey: ['tasks', 'list', 'palette', search],
    queryFn: ({ signal }) => tasksApi.list({ role: 'all', status: 'all', search, pageSize: 6 }, signal),
    enabled,
    staleTime: 15_000,
  });
  const create: CommandItem = {
    id: 'tasks:new',
    label: t('actions.newTask'),
    icon: PlusIcon,
    keywords: [t('page.title'), 'task', 'задача', 'завдання'],
    perform: () => openNew(),
  };
  const items: CommandItem[] = enabled
    ? (found.data?.items ?? []).map((task) => ({
        id: `task:${task.id}`,
        label: task.title,
        description: `#${task.number} · ${t(`status.${task.status}`)}${task.responsible ? ` · ${task.responsible.fullName}` : ''}`,
        icon: SquareCheckBigIcon,
        keywords: [search, String(task.number)],
        perform: () => open('task', task.id),
      }))
    : [];
  const showCreate = matchesQuery(query, create.label, ...(create.keywords ?? []));
  return {
    items: showCreate ? [create, ...items] : items,
    isLoading: enabled && found.isLoading,
  };
}
